/**
 * LA LECTURE DE LA MATRICE DES ACCÈS, SANS AUCUNE RÈGLE À SOI.
 * ============================================================================
 *
 * INTENTION : le front ne décide aucun droit. Pour un geste, un rôle et un
 * abonnement, il lit le verdict que la matrice a calculé (`affichage` actif,
 * inactif ou absent ; `raison`, la phrase de la matrice ; `etiquette_avance`)
 * dans `droits.generated.ts`, généré depuis la copie de `matrice.json`. Le
 * serveur refuse de toute façon ; l'écran évite seulement de montrer un bouton
 * mort, et dit pourquoi.
 *
 * Ce fichier est le SEUL lecteur de `droits.generated.ts` et de `matrice.json`
 * (garde : `acces.spec.ts`). Il ajoute deux choses, et seulement deux :
 *  1. fermé par défaut : un rôle, un abonnement ou un geste inconnus, ou non
 *     lus, rendent le geste ABSENT, jamais actif ;
 *  2. la condition de l'objet que la matrice nomme sans la calculer (être
 *     destinataire de l'alarme, pour « Acquitter ») : l'appelant dit si elle
 *     est remplie, la phrase vient de la matrice (`phrase_condition`).
 *
 * La colonne `support` n'est jamais choisie ici (plan du front, §6 point 15) :
 * une session de l'équipe agit avec le rôle du compte qu'elle regarde.
 */
import {
    ABONNEMENTS_MATRICE,
    DROITS,
    ROLES_MATRICE,
    VERSION_MATRICE,
} from "@modules/alarmes-v2/acces/droits.generated"
import type {
    AlarmesV2Abonnement,
    AlarmesV2Offre,
    AlarmesV2Sms,
} from "@modules/alarmes-v2/types/alarmes-v2"

export { VERSION_MATRICE }

/** La clé d'un geste de la matrice (`liste_voir`, `acquitter_depuis_l_ecran`…). */
export type CleGeste = keyof typeof DROITS

/** Un rôle de la matrice. */
export type RoleMatrice = (typeof ROLES_MATRICE)[number]

/**
 * Un rôle que le front peut tenir : les quatre rôles membres et, sur la seule
 * page du lien (`/a`), `destinataire_lien`. Jamais `support`.
 */
export type RoleDuFront = Exclude<RoleMatrice, "support">

/** Un abonnement de la matrice (identique à `AlarmesV2Abonnement`). */
export type AbonnementMatrice = (typeof ABONNEMENTS_MATRICE)[number]

/** Comment un geste se montre. */
export type Affichage = "actif" | "inactif" | "absent"

/** Le verdict rendu pour un geste. */
export interface Acces {
    cle: string
    /** Vrai seulement quand le geste est actif. */
    ok: boolean
    affichage: Affichage
    /** La phrase de la matrice, en une ligne ; `null` quand le geste est actif. */
    raison: string | null
    /** Vrai quand le geste est fermé parce qu'il est de l'option Avancé. */
    etiquetteAvance: boolean
}

/** Ce que le front sait de la personne et de son entreprise. */
export interface ContexteAcces {
    /** Le rôle lu ; tout autre valeur, `null` ou `undefined` ferme tout. */
    role: string | null | undefined
    /** L'abonnement lu ; `null` (non lu, module fermé) ferme tout. */
    abonnement: AbonnementMatrice | null | undefined
    /**
     * La condition de l'objet que nomme le geste (être destinataire de
     * l'alarme) : `true` remplie, `false` non remplie (inactif avec la phrase
     * de condition), `null` ou `undefined` inconnue (absent). Ignorée pour un
     * geste sans `phrase_condition`.
     */
    condition?: boolean | null
}

/** Le texte de l'étiquette de niveau (S:2861). */
export const ETIQUETTE_AVANCE = "Avancé"

/** Les quatre rôles membres, tels que `auth.store.ts` les tient. */
const ROLES_MEMBRES = [
    "owner",
    "administrateur",
    "employe",
    "employe_read_only",
] as const

/** Les rôles que ce fichier accepte : jamais `support`. */
const ROLES_ACCEPTES: readonly string[] = [
    ...ROLES_MEMBRES,
    "destinataire_lien",
]

/** Un geste fermé sans rien dire : ce qui n'est pas su ne s'affiche pas. */
function absent(cle: string): Acces {
    return {
        cle,
        ok: false,
        affichage: "absent",
        raison: null,
        etiquetteAvance: false,
    }
}

/** Vrai pour une clé de geste de la matrice. */
export function estCleGeste(cle: string): cle is CleGeste {
    return Object.prototype.hasOwnProperty.call(DROITS, cle)
}

/**
 * Le rôle de la matrice pour le rôle de la session (`auth.role`). Les quatre
 * rôles membres portent les mêmes clés que la matrice (plan §1 fait 11) ; tout
 * autre valeur, `null` compris, rend `null`, qui ferme tout. Ne rend jamais
 * `support` ni `destinataire_lien` : ce dernier ne vaut que sur `/a`, où la
 * page le passe elle-même.
 */
export function roleDeLaSession(
    role: string | null | undefined
): RoleDuFront | null {
    return (ROLES_MEMBRES as readonly string[]).includes(role ?? "")
        ? (role as RoleDuFront)
        : null
}

/**
 * L'abonnement que la matrice lit (plan §2.4, point 6), depuis ce que le
 * front a réellement lu :
 *  • `hasOfferEnergyManager` faux : `sans_abonnement` ;
 *  • niveau autre qu'`avance` : `em_basique` ;
 *  • Avancé : `em_avance_solde_epuise` si le solde lu dit `solde_sms_epuise`,
 *    `em_avance` sinon. Un solde inconnu (`null`) ne ferme rien.
 *
 * `sms` est la lecture la plus récente du solde (`GET /sms`, après une
 * recharge par exemple) ; `undefined` reprend celle de l'offre. Sans offre lue
 * pour une entreprise de l'offre, rien n'est su : `null`.
 */
export function abonnementCourant(lu: {
    hasOfferEnergyManager: boolean
    offre: AlarmesV2Offre | null | undefined
    sms?: AlarmesV2Sms | null
}): AlarmesV2Abonnement | null {
    if (!lu.hasOfferEnergyManager) return "sans_abonnement"
    if (!lu.offre) return null
    if (lu.offre.niveau !== "avance") return "em_basique"
    const sms = lu.sms === undefined ? lu.offre.sms : lu.sms
    return sms?.raison_indisponible === "solde_sms_epuise"
        ? "em_avance_solde_epuise"
        : "em_avance"
}

/**
 * Le verdict d'un geste pour un contexte, par simple lecture de la matrice.
 *
 * @param cle - la clé du geste
 * @param contexte - le rôle, l'abonnement et, s'il y a lieu, la condition
 */
export function acces(cle: CleGeste | string, contexte: ContexteAcces): Acces {
    if (!estCleGeste(cle)) return absent(cle)
    const role = contexte.role ?? ""
    if (!ROLES_ACCEPTES.includes(role)) return absent(cle)
    const abonnement = contexte.abonnement ?? ""
    if (!(ABONNEMENTS_MATRICE as readonly string[]).includes(abonnement))
        return absent(cle)

    const geste = DROITS[cle]
    const verdict =
        geste.verdicts[role as RoleDuFront][abonnement as AbonnementMatrice]
    const lu: Acces = {
        cle,
        ok: verdict.affichage === "actif",
        affichage: verdict.affichage,
        raison: verdict.raison,
        etiquetteAvance: verdict.etiquette_avance,
    }
    // La condition de l'objet ne s'ajoute qu'à un geste que la matrice ouvre.
    if (!lu.ok || geste.phrase_condition === null) return lu
    if (contexte.condition === true) return lu
    if (contexte.condition === false) {
        return {
            ...lu,
            ok: false,
            affichage: "inactif",
            raison: geste.phrase_condition,
        }
    }
    return absent(cle)
}

/**
 * La raison commune d'une rangée de gestes (S:4912-4923) : quand plus d'un
 * geste est fermé, que tous les fermés sont visibles et disent la même phrase,
 * elle s'écrit une fois après les boutons. `null` sinon : chaque geste porte
 * alors sa propre raison.
 */
export function raisonCommune(
    liste: readonly Acces[]
): { raison: string; etiquetteAvance: boolean } | null {
    const fermes = liste.filter((a) => !a.ok)
    const visibles = fermes.filter((a) => a.affichage !== "absent")
    if (visibles.length < 2 || visibles.length !== fermes.length) return null
    const raison = visibles[0].raison
    if (!raison || !visibles.every((a) => a.raison === raison)) return null
    return { raison, etiquetteAvance: visibles[0].etiquetteAvance }
}
